import React, { useState } from 'react';
import {
  Globe, Code2, Zap, Building2, Users, Cloud, Database,
  FileText, FileSearch, Table, Activity, Webhook, Layers, Snowflake, Trash2,
  GitBranch, MessageCircle, Mic, PackageOpen, Mail, Kanban, Archive, Hash, MessagesSquare,
  Shield, Box, Server, type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConnectorConfig } from '../../types/connector';
import { Badge } from '../../design-system/components/Badge';
import { UnencryptedBadge } from './InsecureTransportAck';
import { StatusDot } from '../../design-system/components/StatusDot';
import { categoryColors } from '../../design-system/tokens';
import { usePipelineStore } from '../../store/pipelineStore';
import { mix } from '../../design-system/themeMix';
import { badgeFor, lastSeenSentence } from './edgeService';

/** Lucide icon per connector type icon name (connectorTypes.ts iconName). */
export const CONNECTOR_ICONS: Record<string, LucideIcon> = {
  Globe,
  Code2,
  Zap,
  Building2,
  Users,
  Cloud,
  Database,
  FileText,
  FileSearch,
  Table,
  Activity,
  Webhook,
  Layers,
  Snowflake,
  GitBranch,
  MessageCircle,
  Mic,
  PackageOpen,
  Mail,
  Kanban,
  Archive,
  Box,
  Hash,
  MessagesSquare,
};

const ICON_MAP: Record<string, React.ReactNode> = Object.fromEntries(
  Object.entries(CONNECTOR_ICONS).map(([name, Icon]) => [name, <Icon size={24} />]),
);

interface ConnectorCardProps {
  connector: ConnectorConfig;
  iconName: string;
  onClick: (connector: ConnectorConfig) => void;
  onDelete?: (connector: ConnectorConfig) => void;
  selected?: boolean;
}

export const ConnectorCard: React.FC<ConnectorCardProps> = ({
  connector,
  iconName,
  onClick,
  onDelete,
  selected = false,
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The connector record's counter only updates on pipeline runs; the live
  // pipeline list is the source of truth (same derivation as the detail panel).
  const livePipelineCount = usePipelineStore(
    (s) => s.pipelines.filter((p) => p.connectorIds?.includes(connector.id)).length,
  );
  const pipelineCount = Math.max(livePipelineCount, connector.activePipelineCount || 0);
  const catColor = categoryColors[connector.category] || { bg: mix.fillSoft, text: mix.textTertiary };
  const iconColor = catColor.text;

  // `edge` is non-null only for EDGE connectors; every other type ignores it.
  const edge = connector.edge ?? null;

  // EDGE has its own formatter (neutral Spanish, shared with the detail panel).
  // `formatLastSync` below returns English and stays with `connector.lastSync`,
  // which is a different thing: when a pipeline last pulled, not when the agent
  // last phoned home.
  const formatLastSync = (ts?: string): string => {
    if (!ts) return t('common.never', 'Nunca');
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('common.justNow', 'Hace un momento');
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  return (
    <div
      data-tour-connector={connector.name}
      onClick={() => onClick(connector)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? 'var(--color-base)' : 'var(--color-surface)',
        border: `1px solid ${selected ? 'var(--color-interactive)' : hovered ? 'var(--color-border-emphasis)' : 'var(--color-border)'}`,
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'border-color 80ms ease-out, background-color 80ms ease-out',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Top section */}
      <div style={{
        padding: '16px 16px 12px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '8px',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '4px',
          backgroundColor: catColor.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: iconColor, flexShrink: 0,
        }}>
          {ICON_MAP[iconName] || <Globe size={24} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginLeft: 'auto' }}>
          {/* This connector reaches its source through an on-demand VPN tunnel. */}
          {connector.vpnProfileId && (
            <span
              title="Llega a su fuente a través de una VPN"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '3px', flexShrink: 0,
                backgroundColor: 'var(--color-interactive-dim)',
                color: 'var(--color-interactive)',
              }}
            >
              <Shield size={11} />
            </span>
          )}
          {/* This connector reaches its source through an on-premise agent (CONN-4). */}
          {connector.agentId && (
            <span
              title={t('connectors.agentBadge', 'Llega a su fuente a través de un agente on-premise')}
              data-testid="agent-badge"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '3px', flexShrink: 0,
                backgroundColor: 'var(--color-interactive-dim)',
                color: 'var(--color-interactive)',
              }}
            >
              <Server size={11} />
            </span>
          )}
          {connector.transportUnencrypted && (
            <UnencryptedBadge record={connector.insecureTransportAcknowledged} compact />
          )}
          <Badge label={connector.category} variant="category" size="sm" />
          {onDelete && (hovered || confirmDelete) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirmDelete) {
                  onDelete(connector);
                } else {
                  setConfirmDelete(true);
                  setTimeout(() => setConfirmDelete(false), 2500);
                }
              }}
              title={confirmDelete ? t('connectors.confirmDeleteClickAgain', 'Haz clic de nuevo para confirmar eliminación') : t('connectors.deleteConnector', 'Eliminar conector')}
              style={{
                width: 22, height: 22, borderRadius: '3px', flexShrink: 0,
                border: `1px solid ${confirmDelete ? 'var(--color-status-red-border)' : 'var(--color-border)'}`,
                backgroundColor: confirmDelete ? 'var(--color-status-red-dim)' : 'var(--color-surface)',
                color: confirmDelete ? 'var(--color-status-red)' : 'var(--color-text-subtle)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 80ms',
              }}
            >
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>

      {/* Middle: name + description */}
      <div style={{ padding: '0 16px 12px', flex: 1 }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--color-text)',
          marginBottom: '4px',
          lineHeight: '20px',
        }}>
          {connector.name}
        </div>
        <div style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          lineHeight: '16px',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {connector.description || t('connectors.defaultDescription', { name: connector.name, defaultValue: `Conectar a la fuente de datos ${connector.name}` })}
        </div>
      </div>

      {/* Bottom strip */}
      <div style={{
        borderTop: '1px solid var(--color-border)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: 'var(--color-base)',
      }}>
        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--color-text)', fontWeight: 500 }}>{pipelineCount}</span> {t('connectors.pipelinesCount', { count: pipelineCount, defaultValue: 'pipelines' })}
        </span>
        <span style={{ color: 'var(--color-border-emphasis)', fontSize: '11px' }}>|</span>
        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {edge ? lastSeenSentence(edge.last_seen_at) : formatLastSync(connector.lastSync)}
        </span>
        <span style={{ color: '#CBD5E1', fontSize: '11px' }}>|</span>
        <StatusDot status={connector.status} size={8} />
        {/* Liveness badge, beside — never instead of — the connector's own
            status. The two mean different things and share no vocabulary:
            the label is always supplied here, and never the `error` variant. */}
        {edge && <StatusDot {...badgeFor(edge.agent_state)} size={8} showLabel />}
      </div>
    </div>
  );
};

export default ConnectorCard;
